import {
    getNextElementInArray,
    getPreviousElementInArray,
} from "@apps/instagram/components/InstagramPostModal/utils";
import InstagramTaggedProductServiceInterface from "@apps/instagram/services/InstagramTaggedProductService/InstagramTaggedProductServiceInteface";
import type TikTokPostTagEntity from "@apps/tiktok/entities/TikTokPostTagEntity";
import { TikTokPostsQuery } from "@apps/tiktok/queries/TikTokPostQuery";
import { TikTokPostsStore } from "@apps/tiktok/stores/TikTokPostsStore";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { BehaviorSubject } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

class TikTokPostModalManager {
    activePost$ = this.postsQuery.active$;
    selectedTagId$ = new BehaviorSubject<number | null>(null);
    openedProductTagId$ = new BehaviorSubject<number | null>(null);
    openedProductTags$ = new BehaviorSubject<TikTokPostTagEntity[] | null>(
        null
    );
    openedProductTag$ = new BehaviorSubject<TikTokPostTagEntity | null>(null);
    constructor(
        private readonly postsQuery: TikTokPostsQuery,
        private readonly postsStore: TikTokPostsStore,
        private readonly taggedProductService: InstagramTaggedProductServiceInterface,
        private readonly productsService: ProductsServiceInterface
    ) {
        const productTags$ = this.openedProductTagId$.pipe(
            map(() => {
                return this.postsQuery.active$.getValue()?.tags || null;
            })
        );
        productTags$.subscribe(this.openedProductTags$);
        productTags$
            .pipe(
                map((v) => {
                    return (
                        v?.find(
                            (t) => t.id === this.openedProductTagId$.getValue()
                        ) || null
                    );
                })
            )
            .subscribe(this.openedProductTag$);
        this.openedProductTag$
            .pipe(
                filter((v): v is TikTokPostTagEntity => !!v),
                switchMap((tag) => {
                    this.productsService.addProductForFetching(tag.product.id);
                    return this.taggedProductService.getTaggedProductByProductId(
                        tag.product.id
                    );
                })
            )
            .subscribe();
    }

    changeActivePost(id: number): void {
        if (!this.postsQuery.hasEntity(id)) {
            throw new Error(`Post with id ${id} does not exists in store`);
        }
        this.postsStore.setActive(id);
    }

    next(): void {
        this.postsStore.setActive({ next: true });
    }

    prev(): void {
        this.postsStore.setActive({ prev: true });
    }

    selectTag(tagId: number): void {
        this.selectedTagId$.next(tagId);
    }

    unSelectTag(): void {
        this.selectedTagId$.next(null);
    }

    openProductTag(productTagId: number | null): void {
        this.openedProductTagId$.next(productTagId);
    }
    /**
     * Go to the next product tag (for navigation in product view)
     */
    nextProductTag(): void {
        const openedProductTags = this.openedProductTags$.getValue();
        if (!openedProductTags) throw new Error();
        const currentProductTag = this.openedProductTag$.getValue();
        const nextProductTag = getNextElementInArray(
            currentProductTag,
            openedProductTags
        );
        if (!nextProductTag) throw new Error();
        this.openedProductTagId$.next(nextProductTag.id);
    }
    /**
     * Go to the prev product tag (for navigation in product view)
     */
    prevProductTag(): void {
        const openedProductTags = this.openedProductTags$.getValue();
        if (!openedProductTags) throw new Error();
        const currentProductTag = this.openedProductTag$.getValue();
        const nextProductTag = getPreviousElementInArray(
            currentProductTag,
            openedProductTags
        );
        if (!nextProductTag) throw new Error();
        this.openedProductTagId$.next(nextProductTag.id);
    }
}

export default TikTokPostModalManager;
